// src/components/Hero/Hero.jsx
import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  FaBolt,
  FaRocket,
  FaCompass,
  FaCodeBranch,
  FaChartLine,
  FaArrowRight,
  FaPlayCircle,
} from "react-icons/fa";
import "./Hero.css";
import { useLocalContext } from "../../context/LocalContext";

const Hero = () => {
  const navigate = useNavigate();
  const { webinfo = {} } = useLocalContext();

  const brandName = webinfo?.name || "Your Studio";

  const container = {
    hidden: { opacity: 0 },
    show: { opacity: 1, transition: { staggerChildren: 0.08 } },
  };

  const fadeUp = {
    hidden: { opacity: 0, y: 20 },
    show: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: "easeOut" },
    },
  };

  return (
    <section className="hero2" aria-label={`${brandName} growth studio hero`}>
      <div className="hero2-bg" aria-hidden="true" />
      <div className="hero2-orbits" aria-hidden="true" />

      <div className="hero2-shell">
        {/* TOP: main two-column layout */}
        <motion.div
          className="hero2-main"
          initial="hidden"
          animate="show"
          variants={container}
        >
          {/* Left column: headline & flow */}
          <motion.div className="hero2-main-left" variants={fadeUp}>
            <div className="hero2-chip">
              <FaBolt aria-hidden="true" />
              <span>Strategy, design & build in one team</span>
            </div>

            <h1 className="hero2-heading">
              <span className="hero2-heading-top">
                Ship digital experiences that
              </span>
              <span className="hero2-heading-bottom">
                <span className="hero2-gradient-text">sell while you sleep.</span>
              </span>
            </h1>

            <p className="hero2-text">
              {brandName} partners with founders and marketing teams to design,
              develop, and optimize websites, funnels, and campaigns that are built
              around one thing: measurable revenue.
            </p>

            {/* Steps / process */}
            <div className="hero2-steps">
              <div className="hero2-step">
                <span className="hero2-step-tag">01 · Discover</span>
                <h3>Map the real funnel</h3>
                <p>
                  We audit your site, ads, CRM and analytics to locate friction and
                  quick wins across the user journey.
                </p>
              </div>
              <div className="hero2-step">
                <span className="hero2-step-tag">02 · Design</span>
                <h3>Design for decisions</h3>
                <p>
                  High-converting layouts, messaging, and components crafted to move
                  visitors from curious to committed.
                </p>
              </div>
              <div className="hero2-step">
                <span className="hero2-step-tag">03 · Deploy</span>
                <h3>Build, launch, iterate</h3>
                <p>
                  Fast, SEO-friendly builds with tracking baked in—and weekly
                  experiments, not yearly redesigns.
                </p>
              </div>
            </div>

            {/* CTAs */}
            <div className="hero2-actions">
              <button
                className="hx-btn hx-btn-primary"
                onClick={() => navigate("/contact")}
                aria-label="Plan my funnel"
              >
                Plan my funnel <FaArrowRight aria-hidden="true" />
              </button>
              <button
                className="hx-btn hx-btn-outline"
                onClick={() => navigate("/services")}
                aria-label="View services"
              >
                View services <FaPlayCircle aria-hidden="true" />
              </button>
            </div>
          </motion.div>

          {/* Right column: cards & metrics */}
          <motion.div className="hero2-main-right" variants={fadeUp}>
            <div className="hero2-panel hero2-panel--top">
              <div className="hero2-panel-header">
                <span className="hero2-panel-label">
                  Live snapshot
                </span>
                <span className="hero2-panel-status">
                  <span className="dot" /> In growth mode
                </span>
              </div>

              <div className="hero2-metrics-grid">
                <div className="hero2-metric">
                  <span className="hero2-metric-label">Demo bookings</span>
                  <span className="hero2-metric-value">+142%</span>
                  <span className="hero2-metric-sub">vs last 30 days</span>
                </div>
                <div className="hero2-metric">
                  <span className="hero2-metric-label">Cost per lead</span>
                  <span className="hero2-metric-value hero2-metric-value--down">
                    -38%
                  </span>
                  <span className="hero2-metric-sub">after funnel revamp</span>
                </div>
                <div className="hero2-metric">
                  <span className="hero2-metric-label">Time to launch</span>
                  <span className="hero2-metric-value">21 days</span>
                  <span className="hero2-metric-sub">average from brief</span>
                </div>
              </div>

              <div className="hero2-progress-row">
                <div className="hero2-progress-label">
                  <FaChartLine aria-hidden="true" />
                  <span>Pipeline coverage</span>
                </div>
                <div className="hero2-progress-bar">
                  <div className="hero2-progress-bar-fill" />
                </div>
                <span className="hero2-progress-value">3.2× target</span>
              </div>
            </div>

            <div className="hero2-panel hero2-panel--bottom">
              <div className="hero2-services">
                <div className="hero2-service">
                  <span className="hero2-service-icon">
                    <FaCompass aria-hidden="true" />
                  </span>
                  <div className="hero2-service-body">
                    <h4>Positioning & messaging</h4>
                    <p>Clarify who you serve, why it matters, and what you want them to do next.</p>
                  </div>
                </div>

                <div className="hero2-service">
                  <span className="hero2-service-icon">
                    <FaCodeBranch aria-hidden="true" />
                  </span>
                  <div className="hero2-service-body">
                    <h4>Web & product builds</h4>
                    <p>Fast React / WordPress builds wired with tracking, SEO and CRM from day one.</p>
                  </div>
                </div>

                <div className="hero2-service">
                  <span className="hero2-service-icon">
                    <FaRocket aria-hidden="true" />
                  </span>
                  <div className="hero2-service-body">
                    <h4>Acquisition & scale</h4>
                    <p>Search, social and email systems that consistently feed your sales calendar.</p>
                  </div>
                </div>
              </div>

              <div className="hero2-footnote">
                <span>Ready in 2–3 strategy calls.</span>
                <span className="hero2-footnote-pill">
                  <span className="dot" />
                  Founder-friendly, no retainers by default
                </span>
              </div>
            </div>
          </motion.div>
        </motion.div>

        {/* BOTTOM: capability strip */}
        <div className="hero2-strip">
          <div className="hero2-strip-item">
            <span className="hero2-strip-label">Websites & Funnel Systems</span>
            <span className="hero2-strip-meta">Marketing sites · Landing pages · Portals</span>
          </div>
          <div className="hero2-strip-divider" />
          <div className="hero2-strip-item">
            <span className="hero2-strip-label">Performance Marketing</span>
            <span className="hero2-strip-meta">Google · Meta · LinkedIn · Retargeting</span>
          </div>
          <div className="hero2-strip-divider" />
          <div className="hero2-strip-item">
            <span className="hero2-strip-label">Automation & Ops</span>
            <span className="hero2-strip-meta">CRM · Email journeys · Dashboards</span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
